import NavbarV2 from '../../components/navbarV2/NavbarV2';
import './OrderDetailV2.css';
import { useCallback, useEffect, useState } from 'react';
import CartList from '../../components/CartList/CartList';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PrintHero from '../../components/PrintHero/PrintHero';
import ProductPopup from '../../components/ProductPopup/ProductPopup';
import CreditNotesManager from '../../components/CreditNotesManager/CreditNotesManager';
import { ClipLoader } from 'react-spinners';
import {
    formatIkeaCatalogRetailSummary,
    getIkeaCatalogRetailSummaryForComponent,
} from '../../utils/cartMultipackDisplay';
import { printOrderSummaryPdf } from '../../pdfPrinting';
import { formatMoney, toAmount as invoiceAmount, combinedAmount as invoiceCombined, vat18FromCombined } from '../../utils/formatMoney';
import { applyCartPricingToOrder } from '../../pricing';
import { ikeaImageSrc } from '../../utils/ikeaImage';

function formatCompactPackMeasure(pack) {
    if (
        pack.width !== undefined &&
        pack.height !== undefined &&
        pack.depth !== undefined
    ) {
        return `${pack.width}×${pack.height}×${pack.depth} cm`;
    }
    if (pack.depth !== undefined && pack.cross !== undefined) {
        return `${pack.depth}×${pack.cross} cm`;
    }
    return null;
}

const normalizeIkeaArticleId = (raw) => {
    const s = String(raw || '').trim();
    const stripped = s.replace(/^s/i, '');
    const digits = stripped.replace(/\D/g, '');
    return digits || stripped;
};

const OrderDetailV2 = () => {
    const [order, setOrder] = useState(null);
    const [admin, setAdmin] = useState(false);
    const [editing, setEditing] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [printingDetails, setPrintingDetails] = useState(false);
    const [printingItems, setPrintingItems] = useState(false);
    const [surcharge, setSurcharge] = useState(0);
    const [shipping, setShipping] = useState(0);
    const [newSKU, setNewSKU] = useState("");
    const [newAmount, setNewAmount] = useState(0);
    const [newMissingSKU, setNewMissingSKU] = useState("");
    const [newMissingAmount, setNewMissingAmount] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [extraInfo, setExtraInfo] = useState(false);
    const [extraInfoArray, setExtraInfoArray] = useState([]);
    const [fetchingExtraInfo, setFetchingExtraInfo] = useState(true);
    const [old, setOld] = useState(false);
    const [ikeaCartUrl, setIkeaCartUrl] = useState("");
    const [cartWithStock, setCartWithStock] = useState(null);
    const [stockLoading, setStockLoading] = useState(false);
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const params = useParams();
    const navigate = useNavigate();
    const hasValidXeroInvoiceId = (id) => !!id && id !== '00000000-0000-0000-0000-000000000000';

    const cartForDisplay = cartWithStock !== null ? cartWithStock : (order?.cart ?? []);

    useEffect(() => {
        setAdmin(localStorage.getItem('admin'));
        if (params.id) { getOrder(params.id); } else { navigate('/'); }
    }, [params]);

    useEffect(() => {
        if (order && !extraInfo) { getExtraInfo(); setExtraInfo(true); }
    }, [order]);

    useEffect(() => {
        let cancelled = false;

        const loadStock = async () => {
            if (!order?.cart?.length) {
                setCartWithStock([]);
                setStockLoading(false);
                return;
            }

            setCartWithStock(null);
            setStockLoading(true);

            const cart = order.cart;
            const ikeaIds = [...new Set(
                cart.filter((item) => String(item.id).length <= 20).map((item) => normalizeIkeaArticleId(item.id)),
            )].filter(Boolean);

            let itemsMap = {};
            if (ikeaIds.length > 0) {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API}/api/ikea/stock`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ articleIds: ikeaIds }),
                    });
                    const data = await response.json();
                    if (data.items) {
                        itemsMap = data.items;
                    }
                } catch (error) {
                    console.error('Error fetching IKEA stock:', error);
                }
            }

            const items = cart.map((item) => {
                let stock = null;
                let probability = null;
                if (String(item.id).length <= 20) {
                    const key = normalizeIkeaArticleId(item.id);
                    const row = itemsMap[key];
                    if (row?.ok) {
                        stock = row.stock;
                        probability = row.probability;
                    }
                }
                return {
                    ...item,
                    stock,
                    probability,
                };
            });

            if (!cancelled) {
                setCartWithStock(items);
                setStockLoading(false);
            }
        };

        loadStock();
        return () => {
            cancelled = true;
        };
    }, [order?._id, order?.cart]);

    const generateIkeaCartUrl = (skus) => {
        const baseUrl = `https://www.ikea.com/it/it/favourites/receive-share/`;
        const skuString = skus.map(({ id, amount }) => `${id.replace(/^s/, '')}:${amount}`).join(',');
        return `${baseUrl}${skuString}/?source=cart`;
    };

    const getOrder = (id) => {
        setExtraInfo(false);
        fetch(`${process.env.REACT_APP_API}/api/getOrder/${id}`, {})
            .then(response => {
                if (response.status === 200) {
                    response.json().then(data => {
                        setOrder(data);
                        if (data.createdAt != undefined) setOld(true);
                        setSurcharge(data.surcharge);
                        setShipping(data.shipping);
                        setIkeaCartUrl(data.ikeaShareUrl || generateIkeaCartUrl(data.cart));
                    });
                }
            });
    };

    const changeStatus = (e) => {
        e.preventDefault();
        fetch(`${process.env.REACT_APP_API}/api/changeStatus/${order._id}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: e.target.value })
        }).then(response => {
            if (response.status === 200) { toast.success('Changed status!'); getOrder(params.id); }
            else if (response.status === 500) { toast.error('Server error.'); }
        });
    };

    const updatePayment = (e) => {
        e.preventDefault();
        fetch(`${process.env.REACT_APP_API}/api/changePayment/${order._id}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ surcharge: document.getElementById('surcharge').value, shipping: document.getElementById('shipping').value })
        }).then(response => {
            if (response.status === 200) { toast.success('Changed payment details!'); getOrder(params.id); }
            else if (response.status === 500) { toast.error('Server error.'); }
        });
    };

    const updateBilling = (e) => {
        e.preventDefault();
        fetch(`${process.env.REACT_APP_API}/api/changeBilling/${order._id}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                name: document.getElementById('name').value, company: document.getElementById('company')?.value || '',
                VAT: document.getElementById('VAT')?.value || '', street: document.getElementById('street').value,
                city: document.getElementById('city').value, postCode: document.getElementById('postCode').value,
                phone: document.getElementById('phone').value, email: document.getElementById('email').value,
            })
        }).then(response => {
            if (response.status === 200) { toast.success('Changed billing details!'); getOrder(params.id); }
            else if (response.status === 500) { toast.error('Server error.'); }
        });
    };

    const updateDelivery = (e) => {
        e.preventDefault();
        fetch(`${process.env.REACT_APP_API}/api/changeDelivery/${order._id}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                deliveryname: document.getElementById('deliveryname').value, deliverycompanyName: document.getElementById('deliverycompanyName').value,
                deliveryVAT: document.getElementById('deliveryVAT').value, deliverystreet: document.getElementById('deliverystreet').value,
                deliverycity: document.getElementById('deliverycity').value, deliverypostCode: document.getElementById('deliverypostCode').value,
                deliveryphone: document.getElementById('deliveryphone').value, deliveryemail: document.getElementById('deliveryemail').value,
            })
        }).then(response => {
            if (response.status === 200) { toast.success('Changed delivery details!'); getOrder(params.id); }
            else if (response.status === 500) { toast.error('Server error.'); }
        });
    };

    const updateCart = (cart) => {
        setOrder((prev) => applyCartPricingToOrder(prev, cart));
    };
    const enableEditing = () => { setEditing(true); };
    const saveEditing = () => {
        setEditing(false);
        fetch(`${process.env.REACT_APP_API}/api/changeCart/${order._id}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cart: order.cart })
        }).then(response => {
            if (response.status === 200) { toast.success('Changed order!'); getOrder(params.id); }
            else {
                response.json().then((data) => {
                    const msg = typeof data === 'string' ? data : data?.message || 'Could not save cart.';
                    toast.error(msg);
                }).catch(() => toast.error('Could not save cart.'));
                getOrder(params.id);
            }
        });
    };
    const cancelEditing = () => { setEditing(false); getOrder(params.id); };

    const handleDownloadOrderPdf = useCallback(async () => {
        if (!order) return;
        setPdfGenerating(true);
        try {
            await printOrderSummaryPdf(order, 'order');
            toast.success('Opening print dialog…');
        } catch (e) {
            toast.error(e?.message || 'Could not generate PDF.');
        } finally {
            setPdfGenerating(false);
        }
    }, [order]);

    window.onbeforeprint = () => { setPrinting(true); };
    window.onafterprint = () => { setPrinting(false); setPrintingDetails(false); setPrintingItems(false); };

    const pay = () => {
        fetch(`${process.env.REACT_APP_API}/api/createPaymentOrderEndpoint`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                orderNumber: order.orderNumber, name: order.name, phone: order.phone, email: order.email,
                total: formatMoney(invoiceCombined(order)), payLater: false, id: order._id,
            })
        }).then(response => {
            if (response.status === 200) { response.json().then(data => { window.location = `https://www.vivapayments.com/web/checkout?ref=${data.orderCode}&color=0A6CFF`; }); }
            else if (response.status === 500) { toast.error('Server error.'); }
        });
    };

    const openXeroInvoice = async () => {
        if (!order?.invoice?._id) { toast.error('Invoice not found.'); return; }
        try {
            const response = await fetch(`${process.env.REACT_APP_API}/api/xeroInvoiceUrl/${order.invoice._id}`, {});
            const data = await response.json();
            if (!response.ok || !data?.url) { toast.error(data?.error || 'Xero invoice is not available yet.'); return; }
            window.open(data.url, '_blank', 'noopener,noreferrer');
        } catch { toast.error('Could not open Xero invoice.'); }
    };

    const syncXeroInvoice = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API}/api/syncXeroInvoice/${order._id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const data = await response.json();
            if (!response.ok) {
                toast.error(data?.error || 'Could not sync Xero invoice.');
                return;
            }
            toast.success(`Synced Xero invoice #${data.invoice?.invoiceNumber || order.invoice?.invoiceNumber}`);
            getOrder(params.id);
        } catch {
            toast.error('Could not sync Xero invoice.');
        }
    };

    const updateProduct = (data, amount) => {
        const isFound = order.cart.filter(x => x.id === data.id).length > 0;
        let newCart = order.cart;
        if (isFound) {
            newCart = newCart.map(x => x.id === data.id ? { ...x, amount: Number(x.amount) + Number(amount) } : x);
        } else {
            newCart = [...newCart, { id: data.id, name: data.name, price: data.price.currentPrice, img: data.image, amount }];
        }
        setOrder({ ...order, cart: newCart });
    };

    const getExtraInfo = () => {
        setFetchingExtraInfo(true);
        let SKUs = [];
        if (order.deliveryNote?.length > 0) order.deliveryNote.forEach(el => SKUs.push(el.SKU));
        if (order.missingItems?.length > 0) order.missingItems.forEach(el => SKUs.push(el.SKU));
        if (order.creditNotes?.length > 0) order.creditNotes.forEach(cn => { if (cn.items?.length > 0) cn.items.forEach(el => SKUs.push(el.SKU)); });
        const skuPromises = SKUs.filter(sku => sku.length > 0).map(sku =>
            getProductsByName(sku).then(response => response.length > 0 ? { owner: sku, extraInfo: response[0] } : null)
        );
        Promise.all(skuPromises).then(results => setExtraInfoArray(results.filter(Boolean))).catch(console.error);
        setFetchingExtraInfo(false);
    };

    const getProductsByName = (name) => {
        return fetch(`${process.env.REACT_APP_API}/api/getProductsByName/${name.trim()}/0/10`, {})
            .then(r => r.json()).catch(console.warn);
    };

    const getStatusClass = (status) => {
        switch (status?.toUpperCase()) {
            case 'SUBMITTED': return 'odv2-status--submitted';
            case 'APPROVED': return 'odv2-status--approved';
            case 'DECLINED': return 'odv2-status--declined';
            case 'SHIPPED': return 'odv2-status--shipped';
            default: return '';
        }
    };

    return (
        <div className={printing ? 'order-detail order-detail-printing mb-50' : 'odv2'}>
            {printing ? <PrintHero /> : <NavbarV2 />}
            {isModalOpen && <ProductPopup setIsModalOpen={setIsModalOpen} updateProduct={updateProduct} />}

            {printing ? (
                <>
                    <div className="order-detail-toolbar">
                        {order && admin === "true" && (
                            <div>
                                <h4
                                    onClick={() => navigate(`/order/${params.id}`)}
                                    className={printing ? 'mt-5 mb-0' : undefined}
                                >
                                    {printingDetails ? 'Delivery Note' : printingItems ? 'Missing Note' : 'Order'} #
                                    {old ? order.id : order.orderNumber}
                                </h4>
                                {order.invoice && order.invoice.invoiceNumber > 0 && !printing && (
                                    <h4 onClick={() => navigate(`/invoice/${params.id}`)} className={printing ? 'mt-5 mb-0' : undefined}>
                                        Invoice #{order.invoice.invoiceNumber}
                                    </h4>
                                )}
                            </div>
                        )}
                        {order && (
                            <div style={{ display: 'flex', alignItems: 'center' }} className="mt-20">
                                {admin === "true" &&
                                    (order.status === 'APPROVED' ? (
                                        <p>{order.status}</p>
                                    ) : (
                                        <select value={order.status} onChange={changeStatus}>
                                            <option value="SUBMITTED">SUBMITTED</option>
                                            <option value="APPROVED">APPROVED</option>
                                            <option value="DECLINED">DECLINED</option>
                                            <option value="SHIPPED">SHIPPED</option>
                                        </select>
                                    ))}
                                {!printing && admin === "true" && (
                                    <button
                                        type="button"
                                        onClick={handleDownloadOrderPdf}
                                        disabled={pdfGenerating}
                                        style={{ margin: '0px 0px 0px 10px' }}
                                    >
                                        {pdfGenerating ? 'PDF…' : 'Print'}
                                    </button>
                                )}
                                {!printing && admin === "true" && order?.invoice?.invoiceNumber > 0 && (
                                    <button type="button" onClick={() => navigate(`/invoice/${params.id}`)} style={{ margin: '0px 0px 0px 10px' }}>
                                        Invoice
                                    </button>
                                )}
                                {!printing && admin === "true" && order?.invoice?.invoiceNumber > 0 && (
                                    <button type="button" onClick={syncXeroInvoice} style={{ margin: '0px 0px 0px 10px', width: 'fit-content', padding: '0px 15px' }}>
                                        Sync Xero
                                    </button>
                                )}
                                {!printing && admin === "true" && hasValidXeroInvoiceId(order?.invoice?.xeroInvoiceId) && (
                                    <button type="button" onClick={openXeroInvoice} style={{ margin: '0px 0px 0px 10px', width: 'fit-content', padding: '0px 15px' }}>
                                        Open Xero Invoice
                                    </button>
                                )}
                                {!printing &&
                                    admin === "false" &&
                                    (order?.invoice?.status === 'UNPAID' || order.invoicePayment?.balance > 0) && (
                                        <button type="button" onClick={pay} style={{ margin: '0px 0px 0px 10px' }}>
                                            Pay
                                        </button>
                                    )}
                            </div>
                        )}
                    </div>

                    <div className="order-detail-toolbar-info mt-0 noshadow">
                        {order && (
                            <div className="input-group">
                                <label className="printing-text">Order Number</label>
                                <p>{old ? order.id : order.orderNumber}</p>
                            </div>
                        )}
                        {order && (
                            <div className="input-group">
                                <label className="printing-text">Order Date</label>
                                <p>
                                    {new Intl.DateTimeFormat('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(
                                        new Date(order.date || order.createdAt),
                                    )}
                                </p>
                            </div>
                        )}
                        {order && (
                            <div className="input-group">
                                <label className="printing-text">Order Total</label>
                                <p>€{formatMoney(invoiceCombined(order))}</p>
                            </div>
                        )}
                        {order && (
                            <div className="input-group">
                                <label className="printing-text">Order Status</label>
                                <p>{order.status}</p>
                            </div>
                        )}
                    </div>

                    <div className="order-detail-wrapper-printing noshadow">
                        <div className="cartlist-printing">
                            {order && !printingDetails && !printingItems && (
                                <CartList data={cartForDisplay} showcase={true} editing={editing} update={updateCart} printing={printing} noMargin stockLoading={stockLoading} />
                            )}

                            {order && order.note && !printingDetails && !printingItems && (
                                <div className="input-group mt-50">
                                    <label className="printing-text">Order Note</label>
                                    <p>{order.note}</p>
                                </div>
                            )}

                            <div className="order-detail-price-wrapper-printing">
                                {!printingDetails && !printingItems && (
                                    <div className="invoice-detail-price mt-50 noshadow m-0 p-0 printing-text">
                                        {order && (
                                            <form>
                                                <h4 className="mt-0 mb-10 mt-10 printing-text-xl">Invoice Details</h4>
                                                <div className="order-detail-price-printing">
                                                    <div>
                                                        <div className="input-group-printing">
                                                            <label className="printing-text">Order Total</label>
                                                            <p>€{formatMoney(invoiceAmount(order.total))}</p>
                                                        </div>
                                                        <div className="input-group-printing">
                                                            <label className="printing-text">Surcharge Total</label>
                                                            <p>€{formatMoney(invoiceAmount(order.surcharge))}</p>
                                                        </div>
                                                        <div className="input-group-printing">
                                                            <label className="printing-text">Shipping Total</label>
                                                            <p>€{formatMoney(invoiceAmount(order.shipping))}</p>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="input-group-printing">
                                                            <label className="printing-text">VAT (18%)</label>
                                                            <p>€{formatMoney(vat18FromCombined(invoiceCombined(order)))}</p>
                                                        </div>
                                                        <div className="input-group-printing">
                                                            <label className="printing-text">Combined Total</label>
                                                            <p>€{formatMoney(invoiceCombined(order))}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                {order.method && order.method !== 'vivaWallet' && (
                                                    <div className="input-group">
                                                        <label className="printing-text">Method</label>
                                                        <p>{order.method}</p>
                                                    </div>
                                                )}
                                            </form>
                                        )}
                                    </div>
                                )}
                                {!printingDetails && !printingItems && (
                                    <div className="invoice-detail-price noshadow m-0 p-0 printing-text">
                                        {order && (
                                            <form>
                                                <h4 className="mt-0 mb-10 printing-text-xl">Billing Details</h4>
                                                <div className="order-detail-price-printing">
                                                    <div>
                                                        <div className="input-group-printing">
                                                            <label className="printing-text">Full Name</label>
                                                            <p>{old ? order.owner?.name : order.name}</p>
                                                        </div>
                                                        {order.company && (
                                                            <div className="input-group-printing">
                                                                <label className="printing-text">Company Name</label>
                                                                <p>{old ? order.owner?.company : order.company}</p>
                                                            </div>
                                                        )}
                                                        {order.VAT && (
                                                            <div className="input-group-printing">
                                                                <label className="printing-text">VAT NO</label>
                                                                <p>{old ? order.owner?.VAT : order.VAT}</p>
                                                            </div>
                                                        )}
                                                        <div className="input-group-printing">
                                                            <label className="printing-text">Street Address</label>
                                                            <p>{old ? order.owner?.street : order.street}</p>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="input-group-printing">
                                                            <label className="printing-text">Town / City</label>
                                                            <p>{old ? order.owner?.city : order.city}</p>
                                                        </div>
                                                        <div className="input-group-printing">
                                                            <label className="printing-text">Post Code</label>
                                                            <p>{old ? order.owner?.postCode : order.postCode}</p>
                                                        </div>
                                                        <div className="input-group-printing">
                                                            <label className="printing-text">Phone</label>
                                                            <p>{old ? order.owner?.phone : order.phone}</p>
                                                        </div>
                                                        <div className="input-group-printing">
                                                            <label className="printing-text">Email</label>
                                                            <p>{old ? order.owner?.email : order.email}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </form>
                                        )}
                                    </div>
                                )}
                                <div className="order-detail-price noshadow m-0 p-0 printing-text">
                                    {order && (
                                        <form>
                                            <h4 className="mt-0 mb-10 mt-10 printing-text-xl">Delivery Details</h4>
                                            <div className="order-detail-price-printing">
                                                <div>
                                                    <div className="input-group-printing">
                                                        <label className="printing-text">Full Name</label>
                                                        <p>{order.deliveryname || order.deliveryName}</p>
                                                    </div>
                                                    {(order.deliverycompanyName || order.deliveryCompanyName) && (
                                                        <div className="input-group-printing">
                                                            <label className="printing-text">Company Name</label>
                                                            <p>{order.deliverycompanyName || order.deliveryCompanyName}</p>
                                                        </div>
                                                    )}
                                                    {order.deliveryVAT && (
                                                        <div className="input-group-printing">
                                                            <label className="printing-text">VAT NO</label>
                                                            <p>{order.deliveryVAT}</p>
                                                        </div>
                                                    )}
                                                    <div className="input-group-printing">
                                                        <label className="printing-text">Street Address</label>
                                                        <p>{order.deliverystreet || order.deliveryStreet}</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="input-group-printing">
                                                        <label className="printing-text">Town / City</label>
                                                        <p>{order.deliverycity || order.deliveryCity}</p>
                                                    </div>
                                                    <div className="input-group-printing">
                                                        <label className="printing-text">Post Code</label>
                                                        <p>{order.deliverypostCode || order.deliveryPostCode}</p>
                                                    </div>
                                                    <div className="input-group-printing">
                                                        <label className="printing-text">Phone</label>
                                                        <p>{order.deliveryphone || order.deliveryPhone}</p>
                                                    </div>
                                                    <div className="input-group-printing">
                                                        <label className="printing-text">Email</label>
                                                        <p>{order.deliveryemail || order.deliveryEmail}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <>
            <div className="odv2-toolbar">
                {order && admin === "true" && (
                    <div>
                        <h4 onClick={() => navigate(`/order/${params.id}`)}>
                            {printingDetails ? 'Delivery Note' : printingItems ? 'Missing Note' : 'Order'} #{old ? order.id : order.orderNumber}
                        </h4>
                        {order.invoice && order.invoice.invoiceNumber > 0 && !printing && (
                            <h4 onClick={() => navigate(`/invoice/${params.id}`)} style={{ fontSize: '14px', fontWeight: 600, marginTop: '4px', color: 'var(--v2-text-muted)' }}>
                                Invoice #{order.invoice.invoiceNumber}
                            </h4>
                        )}
                    </div>
                )}
                {order && (
                    <div className="odv2-toolbar-actions">
                        {admin === "true" && (order.status === "APPROVED"
                            ? <span className={`odv2-status-badge ${getStatusClass(order.status)}`}>{order.status}</span>
                            : <select className="odv2-select" value={order.status} onChange={changeStatus}>
                                <option value="SUBMITTED">SUBMITTED</option>
                                <option value="APPROVED">APPROVED</option>
                                <option value="DECLINED">DECLINED</option>
                                <option value="SHIPPED">SHIPPED</option>
                            </select>
                        )}
                        {!printing && admin === "true" && (
                            <button
                                type="button"
                                className="odv2-btn"
                                onClick={handleDownloadOrderPdf}
                                disabled={pdfGenerating}
                            >
                                {pdfGenerating ? 'PDF…' : 'Print'}
                            </button>
                        )}
                        {!printing && admin === "true" && order?.invoice?.invoiceNumber > 0 && (
                            <button className="odv2-btn odv2-btn--outline" onClick={() => navigate(`/invoice/${params.id}`)}>
                                Invoice
                            </button>
                        )}
                        {!printing && admin === "true" && !printingDetails && !printingItems && (
                            <a
                                href={ikeaCartUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="odv2-ikea-link odv2-ikea-link--toolbar"
                            >
                                Add to Ikea Cart
                            </a>
                        )}
                        {!printing && admin === "true" && (
                            <button
                                type="button"
                                className="odv2-btn odv2-btn--secondary"
                                onClick={() => navigate(`/createcreditnote/${order._id}`)}
                            >
                                Credit note
                            </button>
                        )}
                        {!printing && admin === "true" && order?.invoice?.invoiceNumber > 0 && <button className="odv2-btn odv2-btn--outline" onClick={syncXeroInvoice}>Sync Xero</button>}
                        {!printing && admin === "true" && hasValidXeroInvoiceId(order?.invoice?.xeroInvoiceId) && <button className="odv2-btn odv2-btn--outline" onClick={openXeroInvoice}>Xero Invoice</button>}
                        {!printing && admin === "false" && (order?.invoice?.status === "UNPAID" || order.invoicePayment?.balance > 0) && <button className="odv2-btn" onClick={pay}>Pay</button>}
                    </div>
                )}
            </div>

            {/* Info Bar */}
            {order && (
                <div className="odv2-info-bar">
                    <div className="odv2-info-card">
                        <p className="odv2-info-label">Order Number</p>
                        <p className="odv2-info-value">{old ? order.id : order.orderNumber}</p>
                    </div>
                    <div className="odv2-info-card">
                        <p className="odv2-info-label">Order Date</p>
                        <p className="odv2-info-value">{new Intl.DateTimeFormat("en-GB", { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(order.date || order.createdAt))}</p>
                    </div>
                    <div className="odv2-info-card">
                        <p className="odv2-info-label">Order Total</p>
                        <p className="odv2-info-value" style={{ color: 'var(--v2-terracotta)' }}>€{formatMoney(invoiceCombined(order))}</p>
                    </div>
                    <div className="odv2-info-card">
                        <p className="odv2-info-label">Status</p>
                        <span className={`odv2-status-badge ${getStatusClass(order.status)}`}>{order.status}</span>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className={printing ? 'odv2-content odv2-content-printing' : 'odv2-content'}>
                <div className={printing ? 'cartlist-printing' : ''}>
                    {stockLoading && !printing && order && !printingDetails && !printingItems && (
                        <p className="v2-muted" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <ClipLoader color="#C4603C" size={14} />
                            Checking stock…
                        </p>
                    )}
                    {order && !printingDetails && !printingItems && (
                        <CartList data={cartForDisplay} showcase={true} editing={editing} update={updateCart} printing={printing} noMargin stockLoading={stockLoading} />
                    )}

                    {order && !editing && order.status !== "APPROVED" && !printingDetails && !printing && (
                        <button className="odv2-btn odv2-btn--outline" style={{ marginTop: '16px' }} onClick={enableEditing}>Edit</button>
                    )}
                    {order && editing && !printingDetails && !printingItems && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                            <button className="odv2-btn odv2-btn--secondary" onClick={() => setIsModalOpen(true)}>Add</button>
                            <button className="odv2-btn" onClick={saveEditing}>Save</button>
                            <button className="odv2-btn odv2-btn--danger" onClick={cancelEditing}>Cancel</button>
                        </div>
                    )}

                    {order && order.note && !printingDetails && !printingItems && (
                        <div className="odv2-card" style={{ marginTop: '20px' }}>
                            <h4>Order Note</h4>
                            <p>{order.note}</p>
                        </div>
                    )}

                    {/* Invoice Details */}
                    {!printingDetails && !printingItems && (
                        <div className={`odv2-card ${printing ? 'noshadow' : ''}`} style={{ marginTop: '20px' }}>
                            {order && <form>
                                <h4>Invoice Details</h4>
                                <div className={printing ? 'odv2-card-grid' : 'odv2-card-grid'}>
                                    <div>
                                        <div className="odv2-field">
                                            <label>Order Total</label>
                                            <p>€{formatMoney(invoiceAmount(order.total))}</p>
                                        </div>
                                        <div className="odv2-field">
                                            <label>Surcharge Total</label>
                                            {order.status === "APPROVED" || admin !== "true" || printing
                                                ? <p>€{formatMoney(invoiceAmount(order.surcharge))}</p>
                                                : <p>€ <input key={`surcharge-${order.surcharge}`} defaultValue={formatMoney(invoiceAmount(order.surcharge))} id="surcharge" name="surcharge" style={{ width: '60px' }} /></p>}
                                        </div>
                                        <div className="odv2-field">
                                            <label>Shipping Total</label>
                                            {order.status === "APPROVED" || admin !== "true" || printing
                                                ? <p>€{formatMoney(invoiceAmount(order.shipping))}</p>
                                                : <p>€ <input key={`shipping-${order.shipping}`} defaultValue={formatMoney(invoiceAmount(order.shipping))} id="shipping" name="shipping" style={{ width: '60px' }} /></p>}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="odv2-field">
                                            <label>VAT (18%)</label>
                                            <p>€{formatMoney(vat18FromCombined(invoiceCombined(order)))}</p>
                                        </div>
                                        <div className="odv2-field">
                                            <label>Combined Total</label>
                                            <p style={{ fontSize: '18px', fontWeight: 800, color: 'var(--v2-terracotta)' }}>€{formatMoney(invoiceCombined(order))}</p>
                                        </div>
                                    </div>
                                </div>
                                {order.method && order.method !== 'vivaWallet' && (
                                    <div className="odv2-field" style={{ marginTop: '12px' }}>
                                        <label>Method</label>
                                        <p>{order.method}</p>
                                    </div>
                                )}
                                {order.status !== "APPROVED" && !printing && <button className="odv2-btn" style={{ marginTop: '16px' }} onClick={updatePayment}>Change</button>}
                            </form>}
                        </div>
                    )}

                    {/* Billing Details */}
                    {!printingDetails && !printingItems && (
                        <div className={`odv2-card ${printing ? 'noshadow' : ''}`}>
                            {order && <form>
                                <h4>Billing Details</h4>
                                <div className="odv2-card-grid">
                                    <div>
                                        <div className="odv2-field">
                                            <label>Full Name</label>
                                            {printing ? <p>{old ? order.owner?.name : order.name}</p> : <input defaultValue={old ? order.owner?.name : order.name} id="name" name="name" />}
                                        </div>
                                        {(!printing || (old ? order.owner?.company : order.company)) && <div className="odv2-field">
                                            <label>Company Name</label>
                                            {printing ? <p>{old ? order.owner?.company : order.company}</p> : <input defaultValue={old ? order.owner?.company : order.company} id="company" name="company" />}
                                        </div>}
                                        {(!printing || (old ? order.owner?.VAT : order.VAT)) && <div className="odv2-field">
                                            <label>VAT NO</label>
                                            {printing ? <p>{old ? order.owner?.VAT : order.VAT}</p> : <input defaultValue={old ? order.owner?.VAT : order.VAT} id="VAT" name="VAT" />}
                                        </div>}
                                        <div className="odv2-field">
                                            <label>Street Address</label>
                                            {printing ? <p>{old ? order.owner?.street : order.street}</p> : <input defaultValue={old ? order.owner?.street : order.street} id="street" name="street" />}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="odv2-field">
                                            <label>Town / City</label>
                                            {printing ? <p>{old ? order.owner?.city : order.city}</p> : <input defaultValue={old ? order.owner?.city : order.city} id="city" name="city" />}
                                        </div>
                                        <div className="odv2-field">
                                            <label>Post Code</label>
                                            {printing ? <p>{old ? order.owner?.postCode : order.postCode}</p> : <input defaultValue={old ? order.owner?.postCode : order.postCode} id="postCode" name="postCode" />}
                                        </div>
                                        <div className="odv2-field">
                                            <label>Phone</label>
                                            {printing ? <p>{old ? order.owner?.phone : order.phone}</p> : <input defaultValue={old ? order.owner?.phone : order.phone} id="phone" name="phone" />}
                                        </div>
                                        <div className="odv2-field">
                                            <label>Email</label>
                                            {printing ? <p>{old ? order.owner?.email : order.email}</p> : <input defaultValue={old ? order.owner?.email : order.email} id="email" name="email" />}
                                        </div>
                                    </div>
                                </div>
                                {!printing && <button className="odv2-btn" style={{ marginTop: '16px' }} onClick={updateBilling}>Change</button>}
                            </form>}
                        </div>
                    )}

                    {/* Delivery Details */}
                    <div className={`odv2-card ${printing ? 'noshadow' : ''}`}>
                        {order && <form>
                            <h4>Delivery Details</h4>
                            <div className="odv2-card-grid">
                                <div>
                                    <div className="odv2-field">
                                        <label>Full Name</label>
                                        {printing ? <p>{order.deliveryname || order.deliveryName}</p> : <input defaultValue={order.deliveryname || order.deliveryName} id="deliveryname" name="deliveryname" />}
                                    </div>
                                    {(!printing || (order.deliverycompanyName || order.deliveryCompanyName)) && <div className="odv2-field">
                                        <label>Company Name</label>
                                        {printing ? <p>{order.deliverycompanyName || order.deliveryCompanyName}</p> : <input defaultValue={order.deliverycompanyName || order.deliveryCompanyName} id="deliverycompanyName" name="deliverycompanyName" />}
                                    </div>}
                                    {(!printing || order.deliveryVAT) && <div className="odv2-field">
                                        <label>VAT NO</label>
                                        {printing ? <p>{order.deliveryVAT}</p> : <input defaultValue={order.deliveryVAT} id="deliveryVAT" name="deliveryVAT" />}
                                    </div>}
                                    <div className="odv2-field">
                                        <label>Street Address</label>
                                        {printing ? <p>{order.deliverystreet || order.deliveryStreet}</p> : <input defaultValue={order.deliverystreet || order.deliveryStreet} id="deliverystreet" name="deliverystreet" />}
                                    </div>
                                </div>
                                <div>
                                    <div className="odv2-field">
                                        <label>Town / City</label>
                                        {printing ? <p>{order.deliverycity || order.deliveryCity}</p> : <input defaultValue={order.deliverycity || order.deliveryCity} id="deliverycity" name="deliverycity" />}
                                    </div>
                                    <div className="odv2-field">
                                        <label>Post Code</label>
                                        {printing ? <p>{order.deliverypostCode || order.deliveryPostCode}</p> : <input defaultValue={order.deliverypostCode || order.deliveryPostCode} id="deliverypostCode" name="deliverypostCode" />}
                                    </div>
                                    <div className="odv2-field">
                                        <label>Phone</label>
                                        {printing ? <p>{order.deliveryphone || order.deliveryPhone}</p> : <input defaultValue={order.deliveryphone || order.deliveryPhone} id="deliveryphone" name="deliveryphone" />}
                                    </div>
                                    <div className="odv2-field">
                                        <label>Email</label>
                                        {printing ? <p>{order.deliveryemail || order.deliveryEmail}</p> : <input defaultValue={order.deliveryemail || order.deliveryEmail} id="deliveryemail" name="deliveryemail" />}
                                    </div>
                                </div>
                            </div>
                            {!printing && <button className="odv2-btn" style={{ marginTop: '16px' }} onClick={updateDelivery}>Change</button>}
                        </form>}
                    </div>

                    {/* Product Dimensions */}
                    {!printing && admin && (
                        <div className="odv2-card odv2-card--compact-dim">
                            {order && <form>
                                <h4>
                                    Product Dimensions
                                    {order.cart && (
                                        <span className="odv2-dim-total-pkg">
                                            (Total packages:{' '}
                                            {order.cart.reduce((total, item) => {
                                                if (Array.isArray(item.dimensions)) {
                                                    return (
                                                        total +
                                                        item.dimensions.reduce(
                                                            (pt, dim) =>
                                                                pt +
                                                                (Array.isArray(dim.packages) ? dim.packages.length : 0),
                                                            0,
                                                        )
                                                    );
                                                }
                                                return total;
                                            }, 0)}
                                            )
                                        </span>
                                    )}
                                </h4>
                                {order.cart &&
                                    order.cart.map((item, index) =>
                                        Array.isArray(item.dimensions) && item.dimensions.length > 0 ? (
                                            <div key={index} className="odv2-dim-item">
                                                <div className="odv2-dim-header odv2-dim-header--compact">
                                                    <img src={ikeaImageSrc(item.img)} alt={item.name} />
                                                    <div>
                                                        <h5>{item.name}</h5>
                                                        <p className="odv2-dim-meta-line">
                                                            SKU{' '}
                                                            <span className="odv2-sku-pill">{item.id}</span> ·{' '}
                                                            {item.dimensions.reduce(
                                                                (t, d) =>
                                                                    t +
                                                                    (Array.isArray(d.packages) ? d.packages.length : 0),
                                                                0,
                                                            )}{' '}
                                                            packages
                                                        </p>
                                                    </div>
                                                </div>
                                                {item.dimensions.map((dimension, di) => {
                                                    const ikeaRetail = getIkeaCatalogRetailSummaryForComponent(
                                                        item,
                                                        dimension,
                                                    );
                                                    const ikeaRetailLabel =
                                                        ikeaRetail != null
                                                            ? formatIkeaCatalogRetailSummary(ikeaRetail)
                                                            : '';
                                                    return (
                                                        <div key={`${index}-${di}`} className="odv2-dim-compact-block">
                                                            <div className="odv2-dim-compact-component">
                                                                {dimension.image ? (
                                                                    <img
                                                                        src={ikeaImageSrc(dimension.image)}
                                                                        alt={
                                                                            (dimension.imageAlt &&
                                                                                String(dimension.imageAlt)) ||
                                                                            dimension.sku ||
                                                                            'Component'
                                                                        }
                                                                        className="odv2-dim-compact-thumb"
                                                                        loading="lazy"
                                                                    />
                                                                ) : null}
                                                                {dimension.sku ? (
                                                                    <span className="odv2-dim-compact-sku">
                                                                        Component SKU{' '}
                                                                        <span className="odv2-sku-pill">
                                                                            {dimension.sku}
                                                                        </span>
                                                                        {Array.isArray(dimension.packages)
                                                                            ? ` · ${dimension.packages.length} line(s)`
                                                                            : ''}
                                                                        {ikeaRetailLabel ? (
                                                                            <span className="odv2-dim-ikea-catalog">
                                                                                {' '}
                                                                                · {ikeaRetailLabel}
                                                                            </span>
                                                                        ) : null}
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                            {Array.isArray(dimension.packages) &&
                                                                dimension.packages.map((pack, pi) => {
                                                                    const measure = formatCompactPackMeasure(pack);
                                                                    const colli = Math.max(
                                                                        1,
                                                                        Number(pack.packages != null ? pack.packages : 1) ||
                                                                            1,
                                                                    );
                                                                    return (
                                                                        <div
                                                                            key={`${index}-${di}-${pi}`}
                                                                            className="odv2-dim-pack-compact"
                                                                        >
                                                                            <span className="odv2-dim-pack-compact-idx">
                                                                                #{pi + 1}
                                                                            </span>
                                                                            <span className="odv2-dim-pack-compact-body">
                                                                                {pack.articleNumber ? (
                                                                                    <>
                                                                                        Art.{' '}
                                                                                        <span className="odv2-sku-pill">
                                                                                            {pack.articleNumber}
                                                                                        </span>
                                                                                    </>
                                                                                ) : (
                                                                                    'Package'
                                                                                )}
                                                                                {colli > 1 ? ` · ×${colli} colli` : ''}
                                                                                {measure ? ` · ${measure}` : ''}
                                                                                {pack.weight != null
                                                                                    ? ` · ${pack.weight} kg`
                                                                                    : ''}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : null,
                                    )}
                                {order.cart &&
                                    order.cart.filter(
                                        (item) => Array.isArray(item.dimensions) && item.dimensions.length > 0,
                                    ).length === 0 && (
                                        <p className="odv2-dim-empty">
                                            No dimension information available for any products in this order.
                                        </p>
                                    )}
                            </form>}
                        </div>
                    )}
                </div>

                {!printing && admin === "true" && order && (
                    <CreditNotesManager order={order} onUpdate={() => getOrder(params.id)} admin={admin} />
                )}
            </div>
                </>
            )}
        </div>
    );
};

export default OrderDetailV2;
